import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="editable-cell"
export default class extends Controller {
  static targets = ["form", "cell", "input"]
  static values = { pendingSave: { type: Boolean, default: false } }

  connect() {
    this.editingCell = null
    this.pendingSubmission = null
    this.originalValue = null
    this.hasChanges = false
    this.isSubmitting = false
    this.mouseDownInsideRow = false
    this.nextCellToFocus = null

    // Listen for Turbo Stream events
    this.boundHandleSubmitStart = this.handleSubmitStart.bind(this)
    this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this)
    this.boundHandleSubmitError = this.handleSubmitError.bind(this)
    this.boundHandleMouseDown = this.handleMouseDown.bind(this)

    this.formTarget.addEventListener('turbo:submit-start', this.boundHandleSubmitStart)
    this.formTarget.addEventListener('turbo:submit-end', this.boundHandleSubmitEnd)
    this.formTarget.addEventListener('turbo:fetch-request-error', this.boundHandleSubmitError)
    this.element.addEventListener('mousedown', this.boundHandleMouseDown, true)

    // Listen for global cell editing events to hide our cells when other rows are edited
    this.boundHandleGlobalEdit = this.handleGlobalEdit.bind(this)
    document.addEventListener('editable-cell:editing', this.boundHandleGlobalEdit)

    // Check if we should focus a cell in this row after connection
    this.checkAndFocusCell()

    // If this row is targeted by the URL hash, select it (shareable link)
    this.focusFromHash()

    console.log('Editable cell controller connected')
  }

  checkAndFocusCell() {
    // Check if this row should have a cell focused after re-render
    if (window.nextCellToFocus) {
      const { rowId, cellField, openForEdit } = window.nextCellToFocus
      console.log('Checking if should focus - this row:', this.element.id, 'target row:', rowId)

      // Check if this is the row we're looking for
      if (this.element.id === rowId) {
        console.log('This row should focus cell:', cellField, 'in row:', this.element.id, 'openForEdit:', openForEdit)

        // Use requestAnimationFrame to ensure DOM is fully ready and painted
        requestAnimationFrame(() => {
          setTimeout(() => {
            const cell = this.element.querySelector(`[data-field="${cellField}"]`)
            if (cell) {
              console.log('Found and focusing cell:', cellField)

              if (openForEdit && cell.querySelector('.cell-input')) {
                // Open cell for editing
                cell.click()
              } else {
                // Read-only cell (or focus-only): just focus without opening for edit
                cell.setAttribute('tabindex', '0')
                cell.focus()
              }

              // Clear the focus target
              window.nextCellToFocus = null
            } else {
              console.log('Cell not found:', cellField, 'Available cells:',
                Array.from(this.cellTargets).map(c => c.dataset.field))
            }
          }, 50)
        })
      }
    }
  }

  focusFromHash() {
    if (window.location.hash !== `#${this.element.id}`) return

    requestAnimationFrame(() => {
      setTimeout(() => {
        const cell = this.element.querySelector('[data-field="position"]')
        if (!cell) return

        cell.setAttribute('tabindex', '0')
        cell.focus()
        this.scrollCellIntoView(cell)
      }, 50)
    })
  }

  handleGlobalEdit(event) {
    // If another cell is being edited (not ours), hide our editing cell
    if (this.editingCell && event.detail.cell !== this.editingCell) {
      console.log('Another cell is being edited, hiding our cell')
      this.hideCell(this.editingCell, false)
    }
  }

  handleMouseDown(event) {
    // Mark that we clicked inside this row
    this.mouseDownInsideRow = true
    console.log('Mouse down inside row')

    setTimeout(() => {
      this.mouseDownInsideRow = false
      console.log('Reset mouseDownInsideRow flag')
    }, 300)
  }

  handleCellKeydown(event) {
    const cell = event.currentTarget

    // Only handle if we're not already editing a cell
    if (this.editingCell) return

    // Check if this is a printable character (letters, numbers, special characters)
    // Exclude modifier keys and control keys
    const isPrintableKey = event.key.length === 1 &&
                          !event.ctrlKey &&
                          !event.metaKey &&
                          !event.altKey

    // If printable key pressed, start editing and replace content
    if (isPrintableKey) {
      event.preventDefault()

      const display = cell.querySelector('.cell-display')
      const input = cell.querySelector('.cell-input')

      if (!input) return

      // Check if this is a text/number input (not select or checkbox)
      const isTextInput = input.tagName === 'INPUT' &&
                         (input.type === 'text' ||
                          input.type === 'number' ||
                          input.type === 'tel' ||
                          input.type === 'email' ||
                          input.type === 'url')
      const isTextarea = input.tagName === 'TEXTAREA'

      // Only handle for text-based inputs
      if (isTextInput || isTextarea) {
        console.log('Starting edit with key:', event.key)

        // Dispatch global event to hide all other editing cells
        const editEvent = new CustomEvent('editable-cell:editing', {
          bubbles: true,
          detail: { cell: cell }
        })
        document.dispatchEvent(editEvent)

        // Clear the input and set to the pressed key
        input.value = event.key
        this.originalValue = '' // Original was cleared
        this.hasChanges = true

        // Hide display, show input
        if (display) display.classList.add('invisible')
        input.classList.remove('hidden')

        // Focus the input and move cursor to end
        input.focus()
        // Set cursor position after the character (number inputs don't support selection)
        if (input.type !== 'number' && input.setSelectionRange) {
          input.setSelectionRange(1, 1)
        }

        this.editingCell = cell
      }

      return
    }

    // Enter: open cell for editing (or activate first link for non-editable cells)
    if (event.key === 'Enter') {
      event.preventDefault()
      const input = cell.querySelector('.cell-input')
      if (input) {
        cell.click()
      } else {
        const link = cell.querySelector('a, button')
        if (link) link.click()
      }
    }

    // Arrow keys: navigate grid
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault()

      const allCells = Array.from(this.cellTargets)
      const currentIndex = allCells.indexOf(cell)
      const currentRow = this.element
      const allRows = Array.from(document.querySelectorAll('[data-controller="editable-cell"]'))
      const currentRowIndex = allRows.indexOf(currentRow)
      let nextCell = null

      if (event.key === 'ArrowLeft') {
        // Move to previous cell in same row (stop at boundary)
        if (currentIndex > 0) {
          nextCell = allCells[currentIndex - 1]
        }
      } else if (event.key === 'ArrowRight') {
        // Move to next cell in same row (stop at boundary)
        if (currentIndex < allCells.length - 1) {
          nextCell = allCells[currentIndex + 1]
        }
      } else if (event.key === 'ArrowUp') {
        // Move to same column in previous row (stop at boundary)
        const cellField = cell.dataset.field
        if (currentRowIndex > 0) {
          const previousRow = allRows[currentRowIndex - 1]
          if (previousRow) {
            nextCell = previousRow.querySelector(`[data-field="${cellField}"]`)
          }
        }
      } else if (event.key === 'ArrowDown') {
        // Move to same column in next row (stop at boundary)
        const cellField = cell.dataset.field
        if (currentRowIndex < allRows.length - 1) {
          const nextRow = allRows[currentRowIndex + 1]
          if (nextRow) {
            nextCell = nextRow.querySelector(`[data-field="${cellField}"]`)
          }
        }
      }

      // Focus the next cell if one was found
      if (nextCell) {
        nextCell.setAttribute('tabindex', '0')
        nextCell.focus()
        this.scrollCellIntoView(nextCell)
      }
    }

    // PageUp/PageDown: jump by visible row count
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault()

      const allRows = Array.from(document.querySelectorAll('[data-controller="editable-cell"]'))
      const currentRowIndex = allRows.indexOf(this.element)
      const cellField = cell.dataset.field

      const visibleCount = allRows.filter(row => {
        const rect = row.getBoundingClientRect()
        return rect.top < window.innerHeight && rect.bottom > 0
      }).length
      const pageSize = Math.max(1, visibleCount)

      const targetIndex = event.key === 'PageUp'
        ? Math.max(0, currentRowIndex - pageSize)
        : Math.min(allRows.length - 1, currentRowIndex + pageSize)

      const targetCell = allRows[targetIndex]?.querySelector(`[data-field="${cellField}"]`)
      if (targetCell) {
        targetCell.setAttribute('tabindex', '0')
        targetCell.focus()
        this.scrollCellIntoView(targetCell)
      }
    }

    // Tab: move to next/previous cell
    if (event.key === 'Tab') {
      event.preventDefault()

      const allCells = Array.from(this.cellTargets)
      const currentIndex = allCells.indexOf(cell)
      let nextCell = event.shiftKey ? allCells[currentIndex - 1] : allCells[currentIndex + 1]

      // If no next cell in current row, move to next/previous row
      if (!nextCell) {
        const currentRow = this.element
        const allRows = Array.from(document.querySelectorAll('[data-controller="editable-cell"]'))
        const currentRowIndex = allRows.indexOf(currentRow)

        if (event.shiftKey) {
          // Shift+Tab: Go to last cell of previous row
          const previousRow = allRows[currentRowIndex - 1]
          if (previousRow) {
            const previousRowController = this.application.getControllerForElementAndIdentifier(previousRow, 'editable-cell')
            if (previousRowController) {
              const previousRowCells = previousRowController.cellTargets
              nextCell = previousRowCells[previousRowCells.length - 1]
            }
          }
        } else {
          // Tab: Go to first cell of next row
          const nextRow = allRows[currentRowIndex + 1]
          if (nextRow) {
            const nextRowController = this.application.getControllerForElementAndIdentifier(nextRow, 'editable-cell')
            if (nextRowController) {
              const nextRowCells = nextRowController.cellTargets
              nextCell = nextRowCells[0]
            }
          }
        }
      }

      // Focus the next cell
      if (nextCell) {
        nextCell.setAttribute('tabindex', '0')
        nextCell.focus()
        this.scrollCellIntoView(nextCell)
      }
    }
  }

  focusCell(event) {
    const cell = event.currentTarget
    cell.setAttribute('tabindex', '0')
    cell.focus()
  }

  editCell(event) {
    if (this.isSubmitting) {
      console.log('Cannot edit while submission in progress')
      return
    }

    const cell = event.currentTarget
    const display = cell.querySelector('.cell-display')
    const input = cell.querySelector('.cell-input')

    if (!input) return

    // Already editing this cell (e.g. clicking the number spinner re-fires the
    // cell's click handler). Don't re-run setup — it would reset originalValue to
    // the already-edited value and lose the change.
    if (this.editingCell === cell) {
      input.focus()
      return
    }

    // Dispatch global event to hide all other editing cells
    const editEvent = new CustomEvent('editable-cell:editing', {
      bubbles: true,
      detail: { cell: cell }
    })
    document.dispatchEvent(editEvent)

    // Cancel any pending submissions
    if (this.pendingSubmission) {
      clearTimeout(this.pendingSubmission)
      this.pendingSubmission = null
      console.log('Cancelled pending submission')
    }

    // If another cell in this row is being edited, hide it
    if (this.editingCell && this.editingCell !== cell) {
      console.log('Hiding previous cell without saving')
      this.hideCell(this.editingCell, false)
      this.hasChanges = false
    }

    // Store original value
    if (input.type === 'checkbox') {
      this.originalValue = input.checked
    } else {
      this.originalValue = input.value
    }
    this.hasChanges = false

    // Hide display, show input
    if (display) display.classList.add('invisible')
    input.classList.remove('hidden')

    // Focus the input
    input.focus()

    // For text inputs and textareas, select all text
    if (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA') {
      input.select()
    }

    this.editingCell = cell
    console.log('Editing cell:', cell.dataset.field)
  }

  saveCell(event) {
    const input = event.currentTarget
    const cell = input.closest('[data-editable-cell-target="cell"]')

    if (!input) return

    // Don't save if element is no longer in DOM
    if (!document.contains(this.element)) {
      console.log('Element removed from DOM, skipping save')
      return
    }

    // Spinner steps on a number input fire `change` repeatedly while it still has
    // focus. Keep the cell open on those — just track the value — so the user can
    // keep stepping. The value is committed when focus leaves the cell (blur).
    if (input.type === 'number' && event.type === 'change') {
      this.hasChanges = this.pendingSaveValue || (input.value !== this.originalValue)
      console.log('Number stepped, keeping cell open:', cell.dataset.field)
      return
    }

    // Don't save on blur if we just clicked inside this row. Number inputs are
    // exempt: a spinner click sets mouseDownInsideRow, but leaving the field is
    // still a deliberate commit, so it must not be swallowed.
    if (event.type === 'blur' && this.mouseDownInsideRow && input.type !== 'number') {
      console.log('Clicked inside row, not saving on blur')
      return
    }

    // Check if value has changed
    const currentValue = input.type === 'checkbox' ? input.checked : input.value
    this.hasChanges = this.pendingSaveValue || (currentValue !== this.originalValue)

    // Don't save if nothing changed
    if (!this.hasChanges) {
      console.log('No changes detected, just hiding cell')
      this.hideCell(cell, false)
      return
    }

    console.log('Value changed, preparing to save:', this.originalValue, '->', currentValue)

    // Commit immediately on a deliberate, finished interaction:
    //  - select / checkbox: a single change is the whole interaction
    //  - number: blur means the user finished stepping/typing
    if (input.tagName === 'SELECT' || input.type === 'checkbox' || input.type === 'number') {
      console.log('Committing change immediately:', cell.dataset.field)

      // Store info to refocus the same cell after render (stay in same row/column, read-only mode)
      window.nextCellToFocus = {
        rowId: this.element.id,
        cellField: cell.dataset.field,
        direction: 'stay',
        openForEdit: false  // Don't open for edit, just focus
      }

      this.submitForm()
      this.hideCell(cell, false)
      return
    }

    // For other inputs, use a small delay
    console.log('Scheduling save in 150ms')
    if (this.pendingSubmission) clearTimeout(this.pendingSubmission)
    this.pendingSubmission = setTimeout(() => {
      if (document.contains(this.element) && !this.isSubmitting && !this.mouseDownInsideRow) {
        console.log('Executing pending save')
        this.submitForm()
        this.hideCell(cell, false)
      } else {
        console.log('Skipping pending save')
      }
      this.pendingSubmission = null
    }, 150)
  }

  trackChange(event) {
    const input = event.currentTarget
    const currentValue = input.type === 'checkbox' ? input.checked : input.value
    this.hasChanges = this.pendingSaveValue || (currentValue !== this.originalValue)
  }

  submitForm() {
    // Check if the element is still in the document
    if (!document.contains(this.element)) {
      console.log('Element no longer in DOM, skipping submission')
      return
    }

    // Check if form is still valid and connected
    if (!this.formTarget || !this.formTarget.isConnected) {
      console.log('Form is not connected to DOM, skipping submission')
      return
    }

    // Prevent multiple simultaneous submissions
    if (this.isSubmitting) {
      console.log('Submission already in progress, skipping...')
      return
    }

    // Only submit if there are actual changes
    if (!this.hasChanges) {
      return
    }

    console.log('Submitting form for device check:', this.formTarget.action)
    this.formTarget.requestSubmit()
  }

  handleSubmitStart() {
    this.isSubmitting = true
    const row = this.element

    // Add visual feedback
    row.style.opacity = '0.6'
    row.style.pointerEvents = 'none'
    row.classList.add('updating')

    console.log('Form submission started, row frozen')

    // Dispatch event to show "Saving..." indicator
    const savingEvent = new CustomEvent('device-check:saving', {
      bubbles: true,
      detail: { saving: true }
    })
    document.dispatchEvent(savingEvent)
  }

  handleSubmitEnd(event) {
    const fetchResponse = event.detail?.fetchResponse
    const success = fetchResponse && fetchResponse.succeeded

    console.log('Form submission ended, success:', success)

    if (success) {
      const saveEvent = new CustomEvent('device-check:saved', {
        bubbles: true,
        detail: { success: true }
      })
      document.dispatchEvent(saveEvent)
    } else {
      const errorEvent = new CustomEvent('device-check:error', {
        bubbles: true,
        detail: { success: false }
      })
      document.dispatchEvent(errorEvent)
    }

    // Small delay to ensure Turbo Stream replacement completes
    setTimeout(() => {
      this.isSubmitting = false

      // Only update UI if element is still in DOM
      if (document.contains(this.element)) {
        const row = this.element

        // Remove freeze state
        row.style.opacity = ''
        row.style.pointerEvents = ''
        row.classList.remove('updating')

        console.log('Row unfrozen')
      } else {
        console.log('Element was replaced, skipping UI cleanup')
      }
    }, 100)
  }

  handleSubmitError(event) {
    console.error('Form submission error:', event.detail)

    const errorEvent = new CustomEvent('device-check:error', {
      bubbles: true,
      detail: { success: false }
    })
    document.dispatchEvent(errorEvent)

    // Unfreeze the row
    this.isSubmitting = false

    if (document.contains(this.element)) {
      const row = this.element

      // Remove freeze state
      row.style.opacity = ''
      row.style.pointerEvents = ''
      row.classList.remove('updating')

      // Flash red briefly
      row.style.backgroundColor = '#fee2e2'
      setTimeout(() => {
        row.style.backgroundColor = ''
      }, 1000)
    }

    console.error('Failed to update device check')
  }

  hideCell(cell, shouldSubmit = false) {
    if (!cell) return

    const display = cell.querySelector('.cell-display')
    const input = cell.querySelector('.cell-input')

    if (!display || !input) return

    // Update display with current input value
    this.updateDisplayValue(display, input)

    // Hide input, show display
    display.classList.remove('invisible')
    input.classList.add('hidden')

    if (this.editingCell === cell) {
      this.editingCell = null
    }
  }

  updateDisplayValue(display, input) {
    if (input.tagName === 'SELECT') {
      // For select, show the selected option's text
      const selectedOption = input.options[input.selectedIndex]
      display.textContent = selectedOption ? selectedOption.text : ''
    } else if (input.type === 'checkbox') {
      // For checkbox, show checked/unchecked
      display.textContent = input.checked ? '✓' : ''
    } else {
      // For text/number inputs, show the value
      display.textContent = input.value || ''
    }
  }

  handleKeydown(event) {
    const input = event.currentTarget
    const cell = input.closest('[data-editable-cell-target="cell"]')

    // Check if value has changed
    const currentValue = input.type === 'checkbox' ? input.checked : input.value
    this.hasChanges = this.pendingSaveValue || (currentValue !== this.originalValue)

    // Enter key: save or exit edit mode
    if (event.key === 'Enter') {
      if (input.tagName === 'TEXTAREA' && !event.ctrlKey && !event.metaKey) {
        return // Allow newlines in textarea
      }
      event.preventDefault()

      if (this.pendingSubmission) {
        clearTimeout(this.pendingSubmission)
        this.pendingSubmission = null
      }

      if (this.hasChanges) {
        window.nextCellToFocus = {
          rowId: this.element.id,
          cellField: cell.dataset.field,
          direction: 'stay',
          openForEdit: false
        }
        this.submitForm()
      }

      // Always exit edit mode immediately; submission (if any) continues in background
      input.blur()
      this.hideCell(cell, false)

      setTimeout(() => {
        cell.setAttribute('tabindex', '0')
        cell.focus()
      }, 10)
    }

    // Escape key: cancel
    if (event.key === 'Escape') {
      event.preventDefault()

      if (this.pendingSubmission) {
        clearTimeout(this.pendingSubmission)
        this.pendingSubmission = null
      }

      // Reset to original value
      if (input.type === 'checkbox') {
        input.checked = this.originalValue
      } else if (input.tagName !== 'SELECT') {
        input.value = this.originalValue
      }

      this.hasChanges = false
      this.hideCell(cell, false)
    }

    // Tab key: move to next/previous cell
    if (event.key === 'Tab') {
      event.preventDefault()

      if (this.pendingSubmission) {
        clearTimeout(this.pendingSubmission)
        this.pendingSubmission = null
      }

      // Find next cell
      const allCells = Array.from(this.cellTargets)
      const currentIndex = allCells.indexOf(cell)
      let nextCell = event.shiftKey ? allCells[currentIndex - 1] : allCells[currentIndex + 1]
      let targetRowId = this.element.id
      let targetCellField = null

      // If no next cell in current row, move to next/previous row
      if (!nextCell) {
        const currentRow = this.element
        const allRows = Array.from(document.querySelectorAll('[data-controller="editable-cell"]'))
        const currentRowIndex = allRows.indexOf(currentRow)

        if (event.shiftKey) {
          // Shift+Tab: Go to last cell of previous row
          const previousRow = allRows[currentRowIndex - 1]
          if (previousRow) {
            const previousRowController = this.application.getControllerForElementAndIdentifier(previousRow, 'editable-cell')
            if (previousRowController) {
              const previousRowCells = previousRowController.cellTargets
              nextCell = previousRowCells[previousRowCells.length - 1]
              targetRowId = previousRow.id
            }
          }
        } else {
          // Tab: Go to first cell of next row
          const nextRow = allRows[currentRowIndex + 1]
          if (nextRow) {
            const nextRowController = this.application.getControllerForElementAndIdentifier(nextRow, 'editable-cell')
            if (nextRowController) {
              const nextRowCells = nextRowController.cellTargets
              nextCell = nextRowCells[0]
              targetRowId = nextRow.id
            }
          }
        }
      }

      if (nextCell) {
        targetCellField = nextCell.dataset.field
      }

      // Submit if there are changes
      if (this.hasChanges) {
        if (targetCellField) {
          // Store info about which cell to focus after render (open for edit)
          window.nextCellToFocus = {
            rowId: targetRowId,
            cellField: targetCellField,
            direction: event.shiftKey ? 'back' : 'forward',
            openForEdit: true  // Open for edit immediately
          }
          console.log('Tab pressed with changes - will focus cell after save:', targetCellField, 'in row:', targetRowId)
        }

        this.submitForm()
        this.hideCell(cell, false)
      } else {
        // No changes, just move
        this.hideCell(cell, false)
        if (nextCell && document.contains(nextCell)) {
          setTimeout(() => {
            if (document.contains(nextCell)) {
              if (nextCell.querySelector('.cell-input')) {
                nextCell.click()
              } else {
                // Read-only cell: focus it directly, editCell won't fire without an input
                nextCell.setAttribute('tabindex', '0')
                nextCell.focus()
              }
            }
          }, 50)
        }
      }
    }
  }

  scrollCellIntoView(cell) {
    const row = cell.closest('[data-controller="editable-cell"]')
    if (!row) return

    const scrollContainer = row.closest('.overflow-auto')
    if (!scrollContainer) return

    const stickyHeaderRow = scrollContainer.querySelector('[style*="table-header-group"] > [style*="table-row"]')
    const headerHeight = stickyHeaderRow ? stickyHeaderRow.getBoundingClientRect().height : 0

    const containerRect = scrollContainer.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()
    const topThreshold = containerRect.top + headerHeight

    if (rowRect.top < topThreshold) {
      scrollContainer.scrollTop -= topThreshold - rowRect.top
    } else if (rowRect.bottom > containerRect.bottom) {
      scrollContainer.scrollTop += rowRect.bottom - containerRect.bottom
    }
  }

  disconnect() {
    console.log('Stimulus controller disconnecting')

    if (this.pendingSubmission) {
      clearTimeout(this.pendingSubmission)
      this.pendingSubmission = null
    }

    this.isSubmitting = false
    this.hasChanges = false
    this.mouseDownInsideRow = false

    // Remove event listeners
    if (this.formTarget) {
      this.formTarget.removeEventListener('turbo:submit-start', this.boundHandleSubmitStart)
      this.formTarget.removeEventListener('turbo:submit-end', this.boundHandleSubmitEnd)
      this.formTarget.removeEventListener('turbo:fetch-request-error', this.boundHandleSubmitError)
    }

    this.element.removeEventListener('mousedown', this.boundHandleMouseDown, true)
    document.removeEventListener('editable-cell:editing', this.boundHandleGlobalEdit)
  }
}
